'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { button, cx } from '@terra/ui-styled-system';
import type { AutomationIdProps } from '@terra/shared-utils';
import { FileUpload } from '../../../components/file-upload';
import {
  MAX_APPLICATION_DOCS_FILES,
  MAX_APPLICATION_DOCS_FILE_SIZE_BYTES,
} from './applicationDocumentation.constants';
import {
  activateFileUploadLabelOnKeyDown,
  applicationDocsChooseFilesLabelClassName,
  applicationDocsCompactFileUploadTriggerClassName,
  applicationDocsFileUploadRootClassName,
} from './applicationDocumentationUpload.styles';
import {
  type DocumentationFileRejectDetails,
  useDocumentationFileRejectToast,
} from './useDocumentationFileRejectToast';

type ApplicationDocumentationCompactFileInputTriggerVariant =
  | 'field'
  | 'solid-button';

export type ApplicationDocumentationCompactFileInputProps = {
  accept: string;
  fileUploadKey: number;
  onFileAccept: (details: { files: File[] }) => void;
  onFileReject?: (details: DocumentationFileRejectDetails) => void;
  disabled?: boolean;
  existingFileCount?: number;
  triggerVariant?: ApplicationDocumentationCompactFileInputTriggerVariant;
} & AutomationIdProps;

export const ApplicationDocumentationCompactFileInput = ({
  accept,
  fileUploadKey,
  onFileAccept,
  onFileReject,
  disabled,
  existingFileCount = 0,
  triggerVariant = 'field',
  ...automationProps
}: ApplicationDocumentationCompactFileInputProps) => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const showRejectToast = useDocumentationFileRejectToast();
  const remainingFileSlots = Math.max(
    0,
    MAX_APPLICATION_DOCS_FILES - existingFileCount,
  );
  const hasReachedFileLimit = remainingFileSlots === 0;
  const isUploadDisabled = hasReachedFileLimit || disabled;
  const isSolidButton = triggerVariant === 'solid-button';

  const handleFileReject = useCallback(
    (details: DocumentationFileRejectDetails) => {
      showRejectToast(details);
      onFileReject?.(details);
    },
    [onFileReject, showRejectToast],
  );

  return (
    <FileUpload.Root
      key={fileUploadKey}
      className={applicationDocsFileUploadRootClassName}
      accept={accept}
      maxFiles={remainingFileSlots || MAX_APPLICATION_DOCS_FILES}
      maxFileSize={MAX_APPLICATION_DOCS_FILE_SIZE_BYTES}
      onFileAccept={onFileAccept}
      onFileReject={handleFileReject}
      disabled={isUploadDisabled}
      w={isSolidButton ? 'max-content' : 'full'}
      {...automationProps}
    >
      <FileUpload.Label
        role="button"
        tabIndex={isUploadDisabled ? -1 : 0}
        onKeyDown={activateFileUploadLabelOnKeyDown}
        className={cx(
          isSolidButton
            ? button({ size: 'sm', variant: 'solid' })
            : applicationDocsCompactFileUploadTriggerClassName,
          applicationDocsChooseFilesLabelClassName,
        )}
      >
        {t('chooseFiles')}
      </FileUpload.Label>
      <FileUpload.HiddenInput />
    </FileUpload.Root>
  );
};
